<template>
  <div v-loading="!isDataReady">
    <!-- 工具栏 -->
    <div class="toolbar">
      <SelectUser v-model:selectedMember="selectedMember" ref="selectUser" :element="element"/>

      <el-radio-group v-model="dateType" style="margin-left: 16px">
        <el-radio label="day" :value="1">{{ $t('home.filter.day') }}</el-radio>
        <el-radio label="week" :value="2">{{ $t('home.filter.week') }}</el-radio>
        <el-radio label="month" :value="3">{{ $t('home.filter.month') }}</el-radio>
        <el-radio label="year" :value="4">{{ $t('home.filter.year') }}</el-radio>
      </el-radio-group>
    </div>

    <!-- 图表区域 -->
    <div class="chart-scroll-wrapper">
      <div
          ref="chartRef"
          class="chart-inner"
          :style="{ width: chartWidth + 'px', height }"
      ></div>
    </div>
  </div>
</template>

<script setup>
import {init as initEcharts} from '@/utils/echarts/dashboardCore'
import {ref, computed, watch, onMounted, onBeforeUnmount, nextTick} from 'vue'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import SelectUser from '@/views/Home/components/SelectUser.vue'
import {useStore} from 'vuex'
import getOverallMax, {convertDateList, convertMonthList, convertWeekDateList, niceInterval} from "@/views/Home/utils";
import {i18n} from "@/lang";

dayjs.extend(isoWeek)

const selectUser = ref()
const selectedMember = ref()
const store = useStore()

const props = defineProps({
  members: Array,
  width: {type: String, default: '100%'},
  height: {type: String, default: '400px'},
  element: {
    type: Object,
    default: () => ({})
  }
})

const chartRef = ref(null)
let chartInstance = null
const dateType = ref(1)
const isDataReady = ref(false)
const lang = computed(() => store.state.system.langLocal)

const statisticsTeamEmailVo = computed(() => store.state.homeEcharts.statisticsTeamEmailVo)
const xData = computed(() => {
  const isEnglish = lang.value !== 'zh-CN'
  const targetData = statisticsTeamEmailVo.value?.dateList ?? []
  if (dateType.value === 1) {
    // 是英文
    if (isEnglish) {
      return convertDateList(targetData)
    } else {
      return targetData
    }
  } else if (dateType.value === 2) {
    if (isEnglish) {
      return convertWeekDateList(targetData)
    } else {
      return targetData
    }
  } else if (dateType.value === 3) {
    if (isEnglish) {
      return convertMonthList(targetData)
    } else {
      return targetData
    }
  } else {
    return targetData
  }
})

const seriesData = computed(() => {
  const baseA = statisticsTeamEmailVo.value?.receiveEmailList ?? []
  const baseB = statisticsTeamEmailVo.value?.sendEmailList ?? []
  return [
    {
      name: i18n.global.t('home.filter.accept.email'),
      type: 'bar',
      barWidth: 25,
      data: baseA,
      barGap: 0,
      itemStyle: {color: '#7e9ff4'},
      label: {show: true, position: 'top'}
    },
    {
      name: i18n.global.t('home.filter.send.email'),
      type: 'bar',
      barWidth: 25,
      data: baseB,
      itemStyle: {color: '#8edcb6'},
      label: {show: true, position: 'top'}
    }
  ]
})

// 获取最大值（保证为正整数，且非零）
const MaxNumber = computed(() => {
  const receiveList = statisticsTeamEmailVo.value?.receiveEmailList ?? [];
  const sendList = statisticsTeamEmailVo.value?.sendEmailList ?? [];

  const maxVal = getOverallMax(receiveList, sendList); // 假设这个函数返回的是 number
  const safeMax = Math.max(0, Math.floor(maxVal));     // 向下取整，防止浮点数
  return safeMax > 0 ? safeMax : 10; // 保底为 10
});

// 获取合理间隔（niceInterval 要返回整数）
const intervalCom = computed(() => {
  return Math.max(1, Math.floor(niceInterval(MaxNumber.value, 5)));
});

// Y 轴配置函数（保证 max 是 interval 的整数倍）
function getYAxisConfig(granularity) {
  const max = MaxNumber.value;
  const interval = intervalCom.value;

  const ceilMax = Math.ceil(max / interval) * interval;

  return {
    min: 0,
    max: ceilMax,
    interval,
  };
}

const chartOption = computed(() => {
  if (!xData.value.length || !seriesData.value[0].data.length) {
    return {
      title: {
        text: i18n.global.t('common.data.empty'),
        left: 'center',
        top: 'middle',
        textStyle: {
          color: '#ccc',
          fontSize: 16
        }
      }
    }
  }

  const yAxisConfig = getYAxisConfig(dateType.value);

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: {type: 'shadow'}
    },
    legend: {
      data: [i18n.global.t('home.filter.accept.email'), i18n.global.t('home.filter.send.email')],
      left: 0,
      bottom: 20,
    },
    grid: {
      left: 0,
      right: 0,
      top: 40,
      bottom: 80,
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: xData.value
    },
    yAxis: {
      type: 'value',
      ...yAxisConfig
    },
    series: seriesData.value
  }
})

const barWidthPerX = lang.value !== 'zh-CN' ? 130 : 100;
const chartWidth = computed(() => {
  return Math.max(678, xData.value.length * barWidthPerX)
})

function updateChart() {
  if (!chartRef.value) return;

  nextTick(() => {
    if (!chartInstance) {
      chartInstance = initEcharts(chartRef.value);
    }

    const option = chartOption.value;

    chartInstance.clear(); // 清除旧图表

    chartInstance.setOption(option, true);
  })
}

function resizeChart() {
  chartInstance?.resize()
}

const resetEcharts = async () => {
  isDataReady.value = false
  if (selectedMember.value) {
    await store.dispatch('homeEcharts/getEmailEchartsApis', {
      type: 1,
      dayType: dateType.value,
      teamId: props.element.teamId,
      userId: selectedMember.value === i18n.global.t('common.all') ? '' : selectedMember.value
    })
  }
  isDataReady.value = true
}

watch(() => selectedMember.value, async (userId) => {
  if (userId) {
    await resetEcharts()
  }
}, {immediate: true})

watch(() => dateType.value, async (newValue) => {
  if (newValue) {
    await resetEcharts()
  }
}, {immediate: true})

watch(() => isDataReady.value, async (ready) => {
  if (ready) {
    await updateChart()
  }
})

watch(() => chartWidth.value, async (newValue) => {
  if (newValue) {
    await nextTick()
    chartInstance?.resize()
  }
})

onMounted(() => {
  window.addEventListener('resize', resizeChart)
})

onBeforeUnmount(() => {
  chartInstance?.dispose()
  window.removeEventListener('resize', resizeChart)
})
</script>

<style scoped lang="less">
.toolbar {
  margin-bottom: 12px;
  display: flex;
  align-items: center;
}

:deep(.el-radio__inner) {
  display: none !important;
}

.chart-scroll-wrapper {
  overflow-x: auto;
  width: 100%;
}
</style>